import React from 'react'

import { nameAcronym } from '../../../components/shared/name-acronym'
import { normalize } from 'normalizr'
import { frontEventSchema } from '../../../admin/schemas/event-schema'
import store from '../../../admin/store'
import classnames from 'classnames'

const GroupDefaultImage = '/images/group-default.svg'
const DefaultUserIcon = '/images/man-silhouette.svg'

import { FRONT_MERGE_ENTITIES } from '../../ducks/entities'

import {
    I_ACCEPTED,
    A_SIGNEDOUT,
    A_PERMISSION_PUBLIC,
    A_PERMISSION_INVITED_REQUEST,
    EVENT_TAG_LANDING,
    ROSTER_DECLINED_LIMIT,
    EVENT_TAG_ONLINE,
} from '../../../consts'

import { redirectWithEvents } from '../../../events-webview'
import { ImageResized } from '../../../components/shared/webp-image-resized'
import { NavLink } from '../shared/sn-link'

/**
 * Returns url query parameter.
 * USAGE: getUrlParameter('lorem')
 *
 * @param name
 * @param props - additional search in location props?
 * @returns {*}
 */
export const getUrlParameter = (name, props = null) => {
    if (props && props.location && props.location.query) {
        return props.location.query[name] || null
    }
    name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')
    let regex = new RegExp('[\\?&]' + name + '=([^&#]*)')
    let results = regex.exec(window.location.search)
    return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

/**
 * Add schema to link if not exists
 *
 * @param link
 * @param addInstagram  - add instagram host to url?
 * @returns {string}
 */
export const getLink = (link, addInstagram = false) => {
    if (addInstagram) {
        const regex = new RegExp('(http://|https://)?(www.)?instagram.com/[a-zA-Z0-9_/?=-]+')
        return regex.test(link) ? link : `https://www.instagram.com/explore/tags/${link.replace('#', '')}/`
    }

    return (link.indexOf('://') === -1) ? 'http://' + link : link
}

/**
 * Return null if is text string null
 *
 * @param text
 * @returns {null}
 */
export const skipNull = text => text === 'NULL' ? null : text

/**
 * Return image element
 *
 * @param image
 * @param alt
 * @returns {XML}
 */
export const getImage = (image, alt, defaultStyle = {}) => {
    if (!image) {
        return <span />
    }

    let path = image.hasOwnProperty('path') ? image.path : image.id + image.suffix

    if (path) {
        path = image.hasOwnProperty('path') ? image.path : window.APP_CONFIG.REACT_APP_IMAGE_STORAGE_URL + path
    } else {
        path = image
    }

    return <img src={path} alt={alt} style={defaultStyle} />
}

/**
 * Get document height
 *
 * @returns {number}
 */
export const getDocHeight = () => Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
)

/**
 * Get event date
 *
 * @param event
 * @param field
 * @param format
 * @returns {*}
 */
export const getDate = (event, field, format = null) => {
    let date = moment.utc(event[field]).local()
    if (event[field]) {
        if (format) {
            return date.format(format)
        }

        return date
    }

    return null
}

/**
 * Get event time
 *
 * @param event
 * @param field
 * @param format
 * @returns {string}
 */
export const getTime = (event, field, format) => (
    moment
        .utc(event[field])
        .local()
        .format(format)
)

/**
 * Make first letter of string as uppercase
 *
 * @param string
 * @returns {string}
 */
export const capitalizeFirstLetter = string => string.charAt(0).toUpperCase() + string.slice(1)

/**
 * Check if event is not ended
 *
 * @param event
 * @returns {boolean}
 */
export const isUpToDate = event => {
    const now = moment.utc().local()

    return moment
        .utc(event.end_date)
        .local()
        .isAfter(now)
}

/**
 * Return event organizer entity
 *
 * @param {Object} event
 * @returns {{image: *, link: *}}
 */
export const getOrganizerEntity = event => {
    let image = null
    let link = null

    if (event.organisingGroup) {
        if (event.organisingGroup.data.image) {
            image = event.organisingGroup.data.image.data.path
        } else {
            image = GroupDefaultImage
        }
        link = `/clubs/${event.organisingGroup.data.slug}`
    } else {
        if (event.owner && event.owner.data.image) {
            image = event.owner.data.image.data.path
        } else {
            image = GroupDefaultImage
        }
        if (event.owner) {
            link = `/users/${event.owner.data.slug}`
        }
    }
    return {
        image: image,
        link: link,
    }
}

/**
 * Render link element or div base on props.href
 *
 * @param {Object} props
 * @returns {XML}
 */
export const LinkOrDiv = props => {
    if (props.href) {
        if (props.webViewEvent) {
            return (
                <div
                    className={props.className}
                    onClick={() => redirectWithEvents(window.location.origin + props.href)}
                    style={props.style}
                    onMouseEnter={props.onMouseEnter}
                    onMouseLeave={props.onMouseLeave}
                    title={props.title}
                >
                    {props.children}
                </div>
            )
        }

        if (props.jsRedirect) {
            return (
                <div
                    className={props.className}
                    onClick={() => (window.location.href = props.href)}
                    style={props.style}
                    onMouseEnter={props.onMouseEnter}
                    onMouseLeave={props.onMouseLeave}
                    title={props.title}
                >
                    {props.children}
                </div>
            )
        }

        if (props.reactLink) {
            return (
                <NavLink
                    to={props.href}
                    className={props.className}
                    onClick={props.onClick}
                    style={props.style}
                    activeClassName={props.activeClassName}
                    onMouseEnter={props.onMouseEnter}
                    onMouseLeave={props.onMouseLeave}
                    title={props.title}
                    scrollToTop={props.scrollToTop}
                >
                    {props.children}
                </NavLink>
            )
        }


        return (
            <a
                href={props.href}
                className={props.className}
                onClick={props.onClick}
                style={props.style}
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
                title={props.title}
            >
                {props.children}
            </a>
        )
    }
    return (
        <div
            className={props.className}
            onClick={props.onClick}
            style={props.style}
            onMouseEnter={props.onMouseEnter}
            onMouseLeave={props.onMouseLeave}
            title={props.title}
        >
            {props.children}
        </div>
    )
}
/**
 * Render span element or div base on props.printSpan
 *
 * @param props
 * @returns {XML}
 */
export const SpanOrDiv = props => {
    if (props.printSpan) {
        return (
            <span className={props.className} onClick={props.onClick}>
                {props.separatorSpan ? props.separatorSpan : ''}
                {props.children}
            </span>
        )
    }
    return (
        <div className={props.className} onClick={props.onClick}>
            {props.children}
        </div>
    )
}

export const getUserCheers = (user, ownProps) => {
    const { cheers } = ownProps.entities

    return user && Object.values(cheers || [])
        .find(cheer => cheer.id === user.id)
}

export const getUserAttendance = (ownProps, att, user = null) => {
    const { attendance } = ownProps.entities

    if (user) {
        return Object.values(attendance || [])
            .find(attendanceItem => attendanceItem?.user_id === user?.id)
    }

    return attendance && Object.values(attendance || [])
        .find(_attendance => _attendance?.id === att?.id)
}

export const getUserInvitation = (user, ownProps) => {
    const { invitations } = ownProps.entities

    return user && Object.values(invitations || [])
        .find(invitation => (invitation.user_id === user.id || invitation.email === user.email) && invitation.active)
}

export const mapAttendanceStateToObject = (app, auth, ui, ownProps) => {
    const {
        rosterPositions,
        attendance,
        questions,
        answers,
        user,
    } = ownProps.entities
    const authedUser = auth.user

    const authedUserCheers = getUserCheers(authedUser, ownProps)
    const authedUserAttendance = getUserAttendance(ownProps, null, authedUser)
    const authedUserInvitation = getUserInvitation(authedUser, ownProps)

    return {
        authedUser,
        authedUserCheers,
        authedUserAttendance,
        authedUserInvitation,
        busySections: ui.busySections,
        attendance,
        rosterPositions,
        questions,
        answers,
        user,
        entity: app.cheer.entity,
        ableToAttend: attendanceAvailable(authedUser, rosterPositions, authedUserInvitation),
        userIsCheer: app.cheer.isCheer,
        cheerBusy: app.cheer.busy,
    }
}

export const attendanceAvailable = (authedUser, rosterPositions, invitation) => {
    if (!authedUser) {
        return false
    }

    if (
        Object.values(rosterPositions || []).find(
            rp =>
                rp.attendance_permission_id === A_PERMISSION_PUBLIC ||
                rp.attendance_permission_id === A_PERMISSION_INVITED_REQUEST
        )
    ) {
        return true
    }

    return !!invitation
}

export const mapAttendanceStateToProps = ({
    app,
    auth,
    ui,
}, ownProps) => mapAttendanceStateToObject(app, auth, ui, ownProps)

/**
 * Can render attendance box with status?
 *
 * @param authedUserInvitation
 * @param authedUserAttendance
 * @returns {*|boolean}
 */
export const canRenderAttendanceBox = (authedUserInvitation, authedUserAttendance) => (
    (authedUserInvitation && authedUserInvitation.state_id !== I_ACCEPTED) ||
        (authedUserAttendance && authedUserAttendance.state_id !== A_SIGNEDOUT)
)

/**
 * Create user image element
 *
 * @param attendance
 * @returns {XML}
 */
export const renderUserImage = (attendance, forceUser = false) => {
    let imageDataOrNull = null
    let proposedUserOrNull = attendance.proposed_user_id ? attendance.proposed_user_id : null
    let profileLinkOrNull = attendance.profile_link ? attendance.profile_link : null

    if (forceUser) {
        imageDataOrNull = attendance?.avatar ? { path: attendance?.avatar } : null
    } else {
        imageDataOrNull = attendance?.user?.data?.image?.data || null

        if (imageDataOrNull === null) {
            imageDataOrNull = attendance?.groupMember?.data?.image?.data || null
        }

        if (imageDataOrNull === null) {
            imageDataOrNull = attendance?.user?.data?.avatar || null
        }
    }

    const acronym = nameAcronym(`${getAttendanceName(attendance)}`)

    if (imageDataOrNull) {
        return (
            <div className="Event-detail__roster-box-logo-image">
                <ImageResized
                    src={imageDataOrNull?.path || imageDataOrNull}
                    size="avatar-md"
                    defaultSrc={DefaultUserIcon}
                />
            </div>
        )
    }

    if (!proposedUserOrNull && !profileLinkOrNull) {
        return (
            <i className="fas fa-id-card m-2 f3" />
        )
    } else {
        return (
            <div className={classnames({
                'Event-detail__roster-box-logo-text': true,
                'bg-secondary': proposedUserOrNull,
            })}>
                {acronym}
            </div>
        )
    }
}

/**
 * Get attendance name by attendance
 *
 * @param attendance
 * @returns {string}
 */
export const getAttendanceName = attendance => {
    if (attendance?.groupMember && typeof attendance?.groupMember === 'object' && attendance?.groupMember?.data) {
        return `${attendance?.groupMember?.data?.name || ''} ${attendance?.groupMember?.data?.surname || ''}`
    }

    if (attendance?.user && typeof attendance?.user === 'object' && attendance?.user?.data) {
        return `${attendance?.user?.data?.name || ''} ${attendance?.user?.data?.surname || ''}`
    }

    return `${attendance?.name || ''} ${attendance?.surname || ''}`
}

/**
 * Get maximum available users
 *
 * @param entities
 * @returns {number}
 */
export const getMaximumUsers = entities => {
    let totalUsers = 0
    Object.values(entities.rosterPositions).map(pos => {
        if (parseInt(pos.limit, 10) === 0) {
            totalUsers = -1
        }
        if (totalUsers !== -1) {
            totalUsers += pos.limit
        }
        return null
    })
    return totalUsers
}

/**
 * Load single event
 *
 * @param {string} slug event slug
 * @param {Object} authedUser authenticated user
 * @returns {Object} promise object
 */
export const loadEvent = (slug, authedUser = null) => {
    let includes = [
        'locations.sportVenue',
        'sports',
        'organisingGroup.headerImage',
        'owner',
        'rosterPositions',
        'competition',
        'team',
        'webResources',
        'messages:limit(3|0):order(created|desc)',
        'messages.simpleUser',
        'additionalInfo',
        'meta',
        'headerImage',
        'forChildren',
        'forAthletes',
        'tags',
        'descriptions:only_active:only_slugs_sections',
        'attendanceButtons',
        'contacts',
        'userRelation',
        'previewCheers',
        'previewAttendance',
        'links',
    ]

    if (authedUser) {
        includes.push(`attendance:user_id(${authedUser.id})`)
        includes.push(`invitations:user_id(${authedUser.id})`)
        includes.push(`managers:user_id(${authedUser.id})`)
    }

    return window.axios.get(
        `${window.APP_CONFIG.REACT_APP_API_URL}api/events/${slug}?optFields=description&include=${includes.join(',')}`)
        .then(({
            data,
        }) => {
            const normalizedData = normalize(data.data, frontEventSchema)
            const event = normalizedData.entities.events[normalizedData.result]
            const isEventManager = authedUser && event && event.managers && event.managers.data.some(manager => manager.id === authedUser.id)

            store.dispatch({
                type: FRONT_MERGE_ENTITIES,
                entities: normalizedData.entities,
            })

            store.dispatch({
                type: 'SET_CHEER_ENTITY',
                entity: normalizedData.entities.events[slug],
            })

            const eventData = {
                entities: normalizedData.entities,
                event,
                isEventManager,
            }

            event.isOnline = data?.data?.tags?.data?.some?.(tag => tag?.id === EVENT_TAG_ONLINE)

            return new Promise(resolve => resolve(eventData))
        })
        .catch(error => new Promise(reject => reject({ httpStatus: error.status })))
}

/**
 * Load event rosters
 *
 * @param {string} slug event slug
 * @param {number} offset
 * @param {number} limit, default 5
 * @returns {Object} promise object
 */
export const loadEventRosters = (slug, offset, limit = 5) => (
    window.axios.get(
        `${window.APP_CONFIG.REACT_APP_API_URL}api/events/${slug}/roster?include=attendance:only_attendees:limit(5|0)&limit=${limit}&cursor=${offset}`)
        .then(({ data }) => new Promise(resolve => resolve(data)))
        .catch(error => new Promise(reject => reject({ httpStatus: error.status })))
)

/**
 * Load event substitutes
 *
 * @param {string} slug event slug
 * @param {number} offset for positions
 * @param {number} limit for positions, default 5
 * @returns {Object} promise object
 */
export const loadEventSubstitutes = (slug, offset = 0, limit = 5) => (
    window.axios.get(
        `${window.APP_CONFIG.REACT_APP_API_URL}api/events/${slug}/roster?include=substitutes:limit(20|0),substitutes.user&limit=${limit}&cursor=${offset}`)
        .then(({ data }) => {
            const positionsWithSubstitutes = data?.data?.filter?.(position => position?.substitutes_count > 0)

            return new Promise(resolve => resolve(positionsWithSubstitutes))
        })
        .catch(error => new Promise(reject => reject({ httpStatus: error?.status })))
)

/**
 * Load event declined users
 *
 * @param {string} slug event slug
 * @param {number} offset for users
 * @param {number} limit for users, default 100
 * @returns {Object} promise object
 */
export const loadEventDeclined = (slug, offset = 0, limit = ROSTER_DECLINED_LIMIT) => (
    window.axios.get(
        `${window.APP_CONFIG.REACT_APP_API_URL}api/events/${slug}/roster/declined?include=user&limit=${limit}&cursor=${offset}`)
        .then(({ data }) => new Promise(resolve => resolve(data)))
        .catch(error => new Promise(reject => reject({ httpStatus: error.status })))
)

/**
 * Load event roster attendees
 *
 * @param {string} eventSlug event slug
 * @param {number} rosterId roster ID
 * @param {Object} cursor ex. { limit: 5, offset: 5 }
 * @param {Array} skipAttendees users IDs which will be skip
 * @returns {Object} promise object
 */
export const loadEventRosterAttendees = (eventSlug, rosterId, cursor, skipAttendees = []) => (
    window.axios.get(
        `${window.APP_CONFIG.REACT_APP_API_URL}api/events/${eventSlug}/roster/${rosterId}?include=attendance:only_attendees:limit(${cursor.limit}|${cursor.offset}):skip(${skipAttendees.join('|')}))`)
        .then(({ data }) => new Promise(resolve => resolve(data)))
        .catch(error => new Promise(reject => reject({ httpStatus: error.status })))
)

export const isForChildren = event => event.forChildren && event.forChildren.data.isForChildren
export const isForAthletes = event => event.forAthletes && event.forAthletes.data.isForAthletes
export const isForSpectators = event => event?.tags?.data?.some?.(tag => tag?.key === 'for_spectators')

/**
 * Check if event is for landing tagged
 *
 * @param {Object} event
 * @return {*}
 */
export const isForLanding = event => event?.tags?.data?.some?.(tag => tag?.id === EVENT_TAG_LANDING)

/**
 * Returns true if the user is an event owner
 *
 * @param {Object} user
 * @param {Object} event
 * @returns {boolean}
 */
export const isUserOwner = (user, event) => event.organised_by_user_id === user.id

/**
 * Returns true if the user is a manager of the event
 *
 * @param {Object} user
 * @param {Object} event
 * @returns {*}
 */
export const isUserManager = (user, event) => {
    if (!event?.managers) {
        return false
    }

    let eventManagers = event.managers

    if (event.managers?.data) {
        eventManagers = event?.managers?.data.map(manager => manager.id)
    }

    return eventManagers.includes(user.id)
}

/**
 * Returns true if the user is a manager of owner of the event
 *
 * @param {Object} user
 * @param {Object} event
 * @returns {boolean|*}
 */
export const canUserManage = (user, event) => isUserOwner(user, event) || isUserManager(user, event)

export const getChildrenAge = event => {
    const forChildren = event.forChildren && event.forChildren.data ? event.forChildren.data : null

    if (!forChildren) {
        return ''
    }

    if (forChildren.formattedAge) {
        return forChildren.formattedAge
    }

    return ''
}

export const getAthletesLevel = event => {
    const forAthletes = event.forAthletes && event.forAthletes.data ? event.forAthletes.data : null

    if (!forAthletes) {
        return ''
    }

    if (forAthletes.athletes_level) {
        return (
            <small className="font-weight-bold text-color-negative d-none d-md-block">
                &nbsp;&nbsp;(
                {forAthletes.athletes_level.map(level => level.name).join(', ')})
            </small>
        )
    }

    return ''
}
